import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import lookie from 'lookie';
import { useLatest, useUnmount } from 'ahooks';
import { useSelector } from 'react-redux';
import { FrameAnimation } from 'common-package';
import { SocketClientType } from '@/service/socketio';
import { useWebRtc } from '@/hooks/useWebRtc';
import BareDetectBorderGreenBottomImg from '@/assets/image/bare-detect-border-green-bottom.png';
import BareDetectBorderGreenTopImg from '@/assets/image/bare-detect-border-green-top.png';
import BareDetectBorderRedTopImg from '@/assets/image/bare-detect-border-red-top.png';
import BareDetectBorderRedBottomImg from '@/assets/image/bare-detect-border-red-bottom.png';
import { SELECTED_MACHINE_CAMERA_TYPE, storagePrefixFixedV2 } from '@/constant';
import { selectCurrentServingOrder } from '@/store/service';
import ArrowImg from '@/assets/image/arrow.png';
import { BareAreaAbnormalErrorList } from '@/pages/service/massage-control/palpation';
import cx from './index.module.scss';

interface Props {
  socketInstance?: React.MutableRefObject<SocketClientType>;
  onToggleVideoPlayState?: (isPlay: boolean) => void;
  showVideoWrapper?: boolean;
  onRTCConnectionStateChange?: (state: RTCPeerConnectionState) => void;
  headerText?: string;
  barePosition?: BarePositionType;
  errorName: MassageRuntime.RobotExceptionEvents;
  isDetectedHuman: boolean;
  isDetectingTimeout: boolean;
  currentStep?: Service.SubStep;
}

type Coordinate = {
  x: number;
  y: number;
};

export interface RefAttrs {
  start: (onOk?: VoidFunc, onError?: VoidFunc) => Promise<void>;
  stop: (cb?: VoidFunc) => void;
}

const VideoStream = forwardRef<RefAttrs, Props>(
  (
    {
      onToggleVideoPlayState,
      showVideoWrapper,
      onRTCConnectionStateChange,
      socketInstance,
      headerText = '手诊影像采集框',
      errorName,
      barePosition,
      isDetectedHuman,
      isDetectingTimeout,
      currentStep,
    },
    ref
  ) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlayVideo, setIsPlayVideo] = useState(false);
    const [isRTCConnected, setIsRTCConnected] = useState(false);
    const currentServingOrder = useSelector(selectCurrentServingOrder);
    const latestOrder = useLatest(currentServingOrder);

    const bareTopBorderRef = useRef<HTMLImageElement>(null);
    const bareBottomBorderRef = useRef<HTMLImageElement>(null);
    const canvasContainerRef = useRef<HTMLDivElement>(null);

    // 根据理疗部位动态展示边界示意图
    const BareDetectImgs = useMemo(
      () =>
        currentServingOrder?.bodyPart === 'Abdomen'
          ? {
              greenTop: BareDetectBorderGreenBottomImg,
              greenBottom: BareDetectBorderGreenTopImg,
              redTop: BareDetectBorderRedBottomImg,
              redBottom: BareDetectBorderRedTopImg,
            }
          : {
              greenTop: BareDetectBorderGreenTopImg,
              greenBottom: BareDetectBorderGreenBottomImg,
              redTop: BareDetectBorderRedTopImg,
              redBottom: BareDetectBorderRedBottomImg,
            },
      [currentServingOrder?.bodyPart]
    );

    const convertCoordinate = useCallback(
      // eslint-disable-next-line fuxi/max-params-with-fixable
      (
        originalWidth: number,
        originalHeight: number,
        targetWidth: number,
        targetHeight: number,
        originalCoordinate: Coordinate
      ): Coordinate => {
        const xRatio = targetWidth / originalWidth;
        const yRatio = targetHeight / originalHeight;

        return {
          x: originalCoordinate.x * xRatio,
          y: originalCoordinate.y * yRatio,
        };
      },
      []
    );

    const isShowBareDetectBorder = useMemo(() => {
      return BareAreaAbnormalErrorList.includes(errorName);
    }, [errorName]);

    const { start, stop } = useWebRtc({
      socketInstance,
      videoRef,
      rtcAPIType: 'order-service',
      deviceId: currentServingOrder?.deviceId,
      onVideoPlay: () => {
        setIsPlayVideo(true);
      },
      onRTCConnectionStateChange(state) {
        if (state === 'connected') {
          setIsRTCConnected(true);
        }
        onRTCConnectionStateChange?.(state);
      },
    });

    useImperativeHandle(
      ref,
      () => ({
        start,
        stop: cb => {
          stop(() => {
            cb?.();
          });
          setIsPlayVideo(false);
        },
      }),
      [start, stop]
    );

    // 当通过人体检测常驻绿色
    const isShowTopArrow = useMemo(
      () => barePosition?.top?.pass === 'True' || isDetectedHuman,
      [barePosition?.top?.pass, isDetectedHuman]
    );
    const isShowBottomArrow = useMemo(
      () => barePosition?.bottom?.pass === 'True' || isDetectedHuman,
      [barePosition?.bottom?.pass, isDetectedHuman]
    );

    useUnmount(() => stop?.());

    useEffect(() => {
      if (!onToggleVideoPlayState) return;
      onToggleVideoPlayState(isPlayVideo);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlayVideo]);

    useEffect(() => {
      if (BareAreaAbnormalErrorList.includes(errorName)) {
        const containerWidth = canvasContainerRef.current?.offsetWidth;
        const containerHeight = canvasContainerRef.current?.offsetHeight;

        const cameraType: CameraType = lookie.get(SELECTED_MACHINE_CAMERA_TYPE);

        const cameraTypeREALSENSE = {
          width: 480,
          height: 848,
        };

        const cameraTypeAngstrong = {
          width: 360,
          height: 640,
        };

        console.log('[Camera_type]:', {
          cameraType,
          barePosition,
        });
        // 110图片高度,因为图片top默认为顶部坐标，但图片需要以底部为准
        const renderImgHeight = 110;

        // 目标点到图片顶端/底部的自身偏移量
        const imgSelfOffsetPointToBottom = latestOrder.current?.bodyPart === 'Abdomen' ? 80 : 14;
        const imgSelfOffsetPointToTop = latestOrder.current?.bodyPart === 'Abdomen' ? 90 : 28;

        const currentCameraWidth = cameraType === 'REALSENSE' ? cameraTypeREALSENSE.width : cameraTypeAngstrong.width;
        const currentCameraHeight =
          cameraType === 'REALSENSE' ? cameraTypeREALSENSE.height : cameraTypeAngstrong.height;

        const currentTopPosition = convertCoordinate(
          currentCameraWidth,
          currentCameraHeight,
          containerWidth,
          containerHeight,
          {
            x: 240,
            y: barePosition?.top.y_position - renderImgHeight + imgSelfOffsetPointToBottom,
          }
        );

        const currentBottomPosition = convertCoordinate(
          currentCameraWidth,
          currentCameraHeight,
          containerWidth,
          containerHeight,
          {
            x: 240,
            y: barePosition?.bottom.y_position - imgSelfOffsetPointToTop,
          }
        );

        // 进行坐标比例换算
        bareTopBorderRef.current.style.top = currentTopPosition.y + 'px';
        bareBottomBorderRef.current.style.top = currentBottomPosition.y + 'px';
      }
    }, [barePosition, convertCoordinate, errorName, latestOrder]);

    return (
      <div className={cx('human-detection')}>
        <div className={cx('wrapper')} ref={canvasContainerRef}>
          <div className={cx('bare-detect-wrapper')}>
            {/* 腹部理疗区域异常图层 */}

            {isShowBareDetectBorder && (
              <>
                <div ref={bareTopBorderRef} className={cx('bare-top-container')}>
                  {/* 绿色正确框框 */}
                  {!isDetectingTimeout && isShowTopArrow && (
                    <img src={BareDetectImgs?.greenTop} alt="top-border" className={cx('bare-top-border')} />
                  )}
                  {/* 红色警告框框+箭头 */}
                  {!isDetectingTimeout && !isShowTopArrow && (
                    <>
                      <img src={BareDetectImgs?.redTop} alt="top-border" className={cx('bare-top-border')} />
                      <div className={cx('top-arrows', { 'is-Abdomen': currentServingOrder?.bodyPart === 'Abdomen' })}>
                        {Array.from({ length: 3 }).map((_, index) => (
                          <img className={cx('arrow')} key={index} alt="arrow" src={ArrowImg} />
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <div className={cx('bare-bottom-container')} ref={bareBottomBorderRef}>
                  {/* 绿色正确框框 */}
                  {!isDetectingTimeout && isShowBottomArrow && (
                    <img src={BareDetectImgs?.greenBottom} alt="bottom-border" className={cx('bare-bottom-border')} />
                  )}
                  {/* 红色警告框框+箭头 */}
                  {!isDetectingTimeout && !isShowBottomArrow && (
                    <>
                      <img src={BareDetectImgs?.redBottom} alt="bottom-border" className={cx('bare-bottom-border')} />
                      <div
                        className={cx('bottom-arrows', { 'is-Abdomen': currentServingOrder?.bodyPart === 'Abdomen' })}
                      >
                        {Array.from({ length: 3 }).map((_, index) => (
                          <img className={cx('arrow', 'reverse')} key={index} alt="arrow" src={ArrowImg} />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          <div className={cx('video-wrapper', { show: showVideoWrapper })}>
            <div className={cx('header')}>{headerText}</div>
            <div className={cx('main-content')}>
              <div className={cx('scan-box')} />
            </div>
            <div className={cx('footer')}>
              请确保
              <span className={cx('highlight')}>头部、肩部、胯部</span>
              均在
              <br />
              图像采集框中
            </div>
          </div>

          <video
            ref={videoRef}
            autoPlay
            muted
            className={cx('play-content', { show: isRTCConnected && isPlayVideo })}
          />
        </div>
        <FrameAnimation
          className={cx('video-loading')}
          url={storagePrefixFixedV2 + '/human-body-detection/video-sequence-frame/detection-animation.png'}
          aspectRatio={currentStep !== 'Massage' ? 0.9239 : 0.8203}
          count={105}
          duration={5}
          width={548}
        />
      </div>
    );
  }
);

export default VideoStream;
